const ANALYTICS_ENDPOINT =
  process.env.REACT_APP_ANALYTICS_ENDPOINT || "/api/analytics";
const ANALYTICS_FLUSH_INTERVAL_MS = 3000;
const VIEW_DEDUPE_WINDOW_MS = 30 * 60 * 1000;
const MAX_BATCH_SIZE = 20;

let analyticsQueue = [];
let flushTimer = null;
let flushInProgress = false;
let lifecycleHooksRegistered = false;

const isBrowser = typeof window !== "undefined";

const addLifecycleHooks = () => {
  if (!isBrowser || lifecycleHooksRegistered) {
    return;
  }

  const flushOnExit = () => {
    flushAnalyticsQueue({ preferBeacon: true });
  };

  window.addEventListener("pagehide", flushOnExit);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushOnExit();
    }
  });

  lifecycleHooksRegistered = true;
};

const getSessionMarker = (key) => {
  if (!isBrowser) {
    return 0;
  }

  const rawValue = window.sessionStorage.getItem(key);
  return rawValue ? Number(rawValue) || 0 : 0;
};

const markSessionEvent = (key) => {
  if (!isBrowser) {
    return;
  }

  window.sessionStorage.setItem(key, String(Date.now()));
};

const shouldTrackSessionEvent = (key) => {
  const previousTimestamp = getSessionMarker(key);

  if (
    previousTimestamp &&
    Date.now() - previousTimestamp < VIEW_DEDUPE_WINDOW_MS
  ) {
    return false;
  }

  markSessionEvent(key);
  return true;
};

const scheduleAnalyticsFlush = () => {
  if (!isBrowser || flushTimer) {
    return;
  }

  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushAnalyticsQueue();
  }, ANALYTICS_FLUSH_INTERVAL_MS);
};

const sendAnalyticsBatch = async (events, { preferBeacon = false } = {}) => {
  if (!isBrowser || events.length === 0) {
    return true;
  }

  const payload = JSON.stringify({ events });

  if (preferBeacon && typeof navigator.sendBeacon === "function") {
    const queued = navigator.sendBeacon(
      ANALYTICS_ENDPOINT,
      new Blob([payload], { type: "application/json" })
    );

    if (queued) {
      return true;
    }
  }

  const response = await fetch(ANALYTICS_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: payload,
    keepalive: true,
    credentials: "same-origin",
  });

  return response.ok;
};

export const flushAnalyticsQueue = async ({ preferBeacon = false } = {}) => {
  if (flushInProgress || analyticsQueue.length === 0) {
    return;
  }

  flushInProgress = true;

  if (flushTimer) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }

  try {
    while (analyticsQueue.length > 0) {
      const events = analyticsQueue.splice(0, MAX_BATCH_SIZE);

      try {
        const success = await sendAnalyticsBatch(events, { preferBeacon });

        if (!success) {
          analyticsQueue = [...events, ...analyticsQueue];
          break;
        }
      } catch (error) {
        console.error("Analytics flush failed:", error);
        analyticsQueue = [...events, ...analyticsQueue];
        break;
      }

      if (preferBeacon) {
        break;
      }
    }
  } finally {
    flushInProgress = false;

    if (analyticsQueue.length > 0 && !preferBeacon) {
      scheduleAnalyticsFlush();
    }
  }
};

const enqueueAnalyticsEvent = (event, { immediate = false } = {}) => {
  if (!isBrowser || !event.cardId || !event.company) {
    return;
  }

  addLifecycleHooks();

  analyticsQueue.push(event);

  if (immediate || analyticsQueue.length >= MAX_BATCH_SIZE) {
    flushAnalyticsQueue({ preferBeacon: immediate });
    return;
  }

  scheduleAnalyticsFlush();
};

export const getCardVisitSource = (search = "") => {
  const params = new URLSearchParams(search);
  return params.get("source") === "qr" ? "qr" : "direct";
};

export const trackBusinessCardView = ({ cardId, company, source = "direct" }) => {
  if (!cardId || !company) {
    return;
  }

  if (shouldTrackSessionEvent(`analytics:view:${cardId}`)) {
    enqueueAnalyticsEvent({
      eventType: "page_view",
      cardId,
      company,
    });
  }

  if (
    source === "qr" &&
    shouldTrackSessionEvent(`analytics:qr:${cardId}`)
  ) {
    enqueueAnalyticsEvent({
      eventType: "qr_scan",
      cardId,
      company,
    });
  }
};

export const trackBusinessCardLinkClick = ({
  cardId,
  company,
  linkType,
}) => {
  if (!cardId || !company || !linkType) {
    return;
  }

  enqueueAnalyticsEvent(
    {
      eventType: "link_click",
      cardId,
      company,
      linkType,
    },
    { immediate: true }
  );
};

export const getTrackedLinkType = (href = "", ariaLabel = "") => {
  const normalizedHref = String(href || "").trim();
  const normalizedLabel = String(ariaLabel || "").trim().toLowerCase();

  if (!normalizedHref || normalizedHref === "#") {
    return null;
  }

  if (normalizedLabel.includes("contact")) {
    return "contact";
  }

  if (normalizedLabel.includes("profile")) {
    return "profile";
  }

  if (normalizedHref.startsWith("tel:")) {
    return "phone";
  }

  if (normalizedHref.startsWith("mailto:")) {
    return "email";
  }

  try {
    const parsedUrl = new URL(normalizedHref, window.location.origin);
    const hostname = parsedUrl.hostname.replace(/^www\./, "").toLowerCase();

    if (hostname.includes("linkedin")) {
      return "linkedin";
    }

    if (hostname.includes("facebook")) {
      return "facebook";
    }

    if (hostname.includes("instagram")) {
      return "instagram";
    }

    if (hostname.includes("tiktok")) {
      return "tiktok";
    }

    return "website";
  } catch (error) {
    return "other";
  }
};
